import * as Sentry from '@sentry/react';
import { createFileRoute, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { queryKeys, useGetImageQuery } from '@vizit/api';
import { type AssetDetailsOrigin, AssetDetailsTabOptions, parseAssetDetailsOrigin } from '@vizit/assets';
import type { AssetDetailsTabChangeOptions } from '@vizit/assets';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { parseBenchmarkOptionKey } from '@vizit/organisms';
import { buildDynamicBreadcrumbs } from '@vizit/utils';
import * as React from 'react';
import { resolveLoaderWorkspaceId } from '../../../../../hooks/use-workspace-link';
import { AssetDetailsPage, PagesProvider, useSparkChainBreadcrumbs } from '../../../../../pages';
import { LIBRARY_BREADCRUMB, beforeLoadAuthenticatedValidation } from '../../../../../router';
import { BreadcrumbsPageTemplate } from '../../../../../templates';

export const Route = createFileRoute('/_authenticated/w/$workspaceSlug/library/assets/$assetId')({
  staticData: {
    pageName: 'Asset Details',
  },
  beforeLoad: ({ context, location }) => beforeLoadAuthenticatedValidation(context, location),
  component: RouteComponent,
  loader: async ({ location, params, context }) => {
    const organizationId = context.auth?.organizationId;
    const workspaceId = resolveLoaderWorkspaceId(context, params.workspaceSlug);

    let assetName = 'Asset Details';

    if (organizationId && workspaceId) {
      try {
        const imageData = await context.queryClient.ensureQueryData({
          queryKey: queryKeys.images.detail({
            organization_id: organizationId,
            workspace_id: workspaceId,
            image_id: params.assetId,
          }).queryKey,
          queryFn: () =>
            useGetImageQuery.fetcher({
              organization_id: organizationId,
              workspace_id: workspaceId,
              image_id: params.assetId,
            })(),
        });

        assetName = imageData?.name ?? 'Asset Details';
      } catch (error) {
        Sentry.captureException(error, {
          level: 'warning',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Navigation,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Navigation].breadcrumbs,
          },
          extra: {
            page: 'asset details',
          },
        });
      }
    }

    const breadcrumbs = buildDynamicBreadcrumbs(
      location.state?.from,
      assetName,
      // Current page is not clickable, so we don't need to pass href
      undefined,
      location.search,
      LIBRARY_BREADCRUMB,
    );

    return { breadcrumbs };
  },
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: AssetDetailsTabOptions; origin?: AssetDetailsOrigin; collection?: string; benchmark?: string } => {
    // A missing or unknown tab stays out, so the page can tell it from a tab someone picked and choose its default.
    const tab =
      typeof search.tab === 'string' &&
      Object.values(AssetDetailsTabOptions as Record<string, unknown>).includes(search.tab)
        ? (search.tab as AssetDetailsTabOptions)
        : undefined;
    const origin = parseAssetDetailsOrigin(search.origin);
    // The collection the asset was opened from; it names the pair the score was taken against.
    const collection = typeof search.collection === 'string' && search.collection ? search.collection : undefined;
    // The custom benchmark that collection showed with no category, which its own pair says nothing about.
    const benchmark = collection ? parseBenchmarkOptionKey(search.benchmark) : undefined;
    return {
      ...(tab ? { tab } : {}),
      origin,
      ...(collection ? { collection } : {}),
      ...(benchmark ? { benchmark } : {}),
    };
  },
});

function RouteComponent() {
  const router = useRouter();
  const search = useSearch({ from: Route.id });
  const { workspaceSlug, assetId } = useParams({ from: Route.id });
  const { breadcrumbs } = Route.useLoaderData();

  // When the asset is a generated image, enrich the loader breadcrumbs with
  // its Spark generation chain rendered as thumbnails (undefined otherwise,
  // which keeps the loader's plain breadcrumbs).
  const sparkChainBreadcrumbs = useSparkChainBreadcrumbs({
    workspaceSlug,
    assetId,
    baseBreadcrumbs: breadcrumbs,
    origin: search.origin,
  });

  const handleOnTabChange = React.useCallback(
    async (key: AssetDetailsTabOptions, options?: AssetDetailsTabChangeOptions) => {
      await router.navigate({
        to: Route.fullPath,
        params: { assetId },
        search: (prev: Record<string, unknown>) => ({ ...prev, tab: key }),
        // The current entry's state, with this asset's breadcrumb trail; the page stays mounted across assets.
        state: true,
        replace: options?.replace,
      });
    },
    [router, assetId],
  );

  return (
    <PagesProvider
      template={BreadcrumbsPageTemplate}
      templateProps={{ className: 'h-screen', breadcrumbs: sparkChainBreadcrumbs }}
    >
      <AssetDetailsPage
        assetId={assetId}
        selectedTab={search.tab}
        origin={search.origin}
        collectionId={search.collection}
        awaitingCategoryBenchmarkKey={search.benchmark}
        onTabChange={handleOnTabChange}
      />
    </PagesProvider>
  );
}
